import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PRACTICE_TEST_SIZES, PRACTICE_SECONDS_PER_QUESTION } from '@proref/schemas';
import { Icon } from '../shared/Icons';
import { Input } from '../shared/Input';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Select } from '../shared/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { notifyError } from '../../lib/toast';
import { useStartPracticeTest, useStartExamAttempt, useExamsQuery } from '../../hooks/use-exams';

interface StartPracticeTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called with the new attempt's id once the test has been generated.
  onStarted: (attemptId: string) => void;
}

// A plain function, not a component — takes `t` as a parameter (only components/hooks may call
// useTranslation() themselves); the one call site already has it from its own render.
function minutesLabel(t: TFunction, questions: number): string {
  return t('exams.landing.minutesLabel', { minutes: Math.round((questions * PRACTICE_SECONDS_PER_QUESTION) / 60) });
}

type Mode = 'practice' | 'official';

// "Start a new test" — two ways in, deliberately behind one button.
//
//   • Practice: pick a length and whether to be timed; the server draws that many random
//     questions from the bank. Nothing is recorded as an Official result.
//   • Official: pick one of the federation's published Official exams and enter the password the
//     federation gave you. It runs in fullscreen with monitoring, and the result is the
//     federation's record.
//
// The two are separated by a mode toggle rather than two buttons, because the difference matters
// enough to be a deliberate choice: a candidate should never start an Official sitting by
// mis-clicking the practice button.
export function StartPracticeTestDialog({
  open,
  onOpenChange,
  onStarted,
}: StartPracticeTestDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('practice');
  const [count, setCount] = useState<number>(20);
  const [timed, setTimed] = useState(false);
  const [officialExamId, setOfficialExamId] = useState('');
  const [password, setPassword] = useState('');
  const start = useStartPracticeTest();
  const startOfficial = useStartExamAttempt();
  const examsQuery = useExamsQuery({});

  // The Official rule tests this referee may sit: published and proctored. A pack without a
  // password can't be published, so every row here will prompt for one.
  const officialExams = useMemo(
    () =>
      (examsQuery.data ?? []).filter(
        (e) => e.type === 'rule_test' && e.is_proctored && e.published_at !== null,
      ),
    [examsQuery.data],
  );

  const pending = start.isPending || startOfficial.isPending;

  async function begin() {
    try {
      const state =
        mode === 'official'
          ? await startOfficial.mutateAsync({ examId: officialExamId, password })
          : await start.mutateAsync({
              question_count: count as (typeof PRACTICE_TEST_SIZES)[number],
              timed,
            });
      onStarted(state.attempt.id);
      onOpenChange(false);
    } catch (err) {
      // Practice fails when the bank is too small; Official fails on a wrong password (403).
      // Both are named by the API, and the dialog stays open so the candidate can correct it.
      notifyError(
        err,
        mode === 'official' ? t('exams.practiceDialog.startErrorOfficial') : t('exams.landing.packCard.startErrorToast'),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(92vw,30rem)]">
        <DialogHeader>
          <DialogTitle>{t('exams.practiceDialog.title')}</DialogTitle>
          <DialogDescription>
            {mode === 'official'
              ? t('exams.practiceDialog.officialDescription')
              : t('exams.practiceDialog.practiceDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle. Official is visually distinct (warn tint) so the two can't be confused. */}
        <div className="mt-4 grid grid-cols-2 gap-2 rounded-btn border border-line bg-surface-2 p-1">
          {(['practice', 'official'] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                aria-pressed={active}
                onClick={() => setMode(m)}
                className={
                  'rounded-[6px] py-2 text-[13px] font-medium transition-colors ' +
                  (active
                    ? m === 'official'
                      ? 'bg-warn/20 text-warn'
                      : 'bg-surface text-ink shadow-sm'
                    : 'text-muted hover:text-ink')
                }
              >
                {m === 'official' ? t('exams.practiceDialog.officialMode') : t('exams.practiceDialog.practiceMode')}
              </button>
            );
          })}
        </div>

        {mode === 'official' ? (
          <div className="mt-4 flex flex-col gap-3">
            {officialExams.length === 0 ? (
              <div className="rounded-lg border border-dashed border-line-2 bg-bg-2 px-4 py-6 text-center text-[12.5px] text-muted">
                {examsQuery.isLoading
                  ? t('exams.practiceDialog.loadingOfficial')
                  : t('exams.practiceDialog.noOfficialExams')}
              </div>
            ) : (
              <>
                <div>
                  <label
                    htmlFor="official-exam"
                    className="mb-1.5 block text-[12px] font-medium text-muted"
                  >
                    {t('exams.practiceDialog.whichExam')}
                  </label>
                  <Select
                    id="official-exam"
                    value={officialExamId}
                    onChange={(e) => setOfficialExamId(e.target.value)}
                  >
                    <option value="" disabled>
                      {t('exams.practiceDialog.selectOfficialExam')}
                    </option>
                    {officialExams.map((e) => (
                      <option key={e.id} value={e.id}>
                        {t('exams.practiceDialog.examOptionLabel', { title: e.title, count: e.question_count })}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor="official-password"
                    className="mb-1.5 block text-[12px] font-medium text-muted"
                  >
                    {t('exams.practiceDialog.examPassword')}
                  </label>
                  <Input
                    id="official-password"
                    type="password"
                    autoComplete="off"
                    placeholder={t('exams.practiceDialog.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="flex items-start gap-2.5 rounded-lg border border-warn/40 bg-warn/[0.07] p-3">
                  <Icon.lock className="mt-0.5 h-3.5 w-3.5 flex-none text-warn" />
                  <p className="text-[12px] leading-relaxed text-muted">
                    {t('exams.practiceDialog.fullscreenWarning')}
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="mt-4">
              <div className="mb-2.5 text-[12px] font-medium text-muted">{t('exams.practiceDialog.howManyQuestions')}</div>
              <div className="grid grid-cols-4 gap-2">
                {PRACTICE_TEST_SIZES.map((size) => {
                  const active = count === size;
                  return (
                    <button
                      key={size}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setCount(size)}
                      className={
                        'rounded-btn border-[1.5px] py-3 font-display text-[20px] transition-colors ' +
                        (active
                          ? 'border-accent bg-accent/10 text-ink'
                          : 'border-line bg-surface text-ink-2 hover:border-line-3')
                      }
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-line bg-surface-2 p-3.5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-full bg-accent-tint text-accent">
                  <Icon.calendar className="h-3.5 w-3.5" />
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-ink">{t('exams.practiceDialog.timeLimit')}</div>
                  <p className="mt-0.5 text-[12px] text-muted">
                    {t('exams.practiceDialog.timeLimitCaption', {
                      seconds: PRACTICE_SECONDS_PER_QUESTION,
                      minutes: minutesLabel(t, count),
                      count,
                    })}
                  </p>
                </div>
              </div>
              {/* A switch rather than a checkbox, matching the Official-exam toggle on the editor. */}
              <button
                type="button"
                role="switch"
                aria-checked={timed}
                aria-label={t('exams.practiceDialog.timeLimit')}
                onClick={() => setTimed((t) => !t)}
                className={
                  'relative h-[26px] w-[46px] flex-none rounded-full transition-colors ' +
                  (timed ? 'bg-accent' : 'bg-line-3')
                }
              >
                <span
                  className={
                    'absolute top-[3px] h-5 w-5 rounded-full bg-white transition-all ' +
                    (timed ? 'left-[23px]' : 'left-[3px]')
                  }
                />
              </button>
            </div>
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('exams.landing.cancel')}
          </Button>
          <Button
            variant="accent"
            onClick={begin}
            // An Official start needs both an exam and a password; practice needs neither.
            disabled={
              pending || (mode === 'official' && (!officialExamId || password.length === 0))
            }
          >
            {pending ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <>
                {mode === 'official' ? t('exams.practiceDialog.startOfficialExam') : t('exams.landing.packCard.startTest')}{' '}
                <Icon.arrow className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
